/**
 * Rooms Tab - Full CRUD for Studio Rooms
 *
 * Manages studio rooms for a schedule including create, read, update, delete
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  useGetRoomsQuery,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
} from '../../../api/schedulingApi';
import type { Room, RoomFormData } from '../../../types/scheduling';
import AvailabilityManager from '../components/AvailabilityManager';

interface RoomsTabProps {
  scheduleId: string;
  studioId: string;
}

interface RoomFormErrors {
  room_name?: string;
  capacity?: string;
  component_rooms?: string;
  notes?: string;
}

const RoomsTab: React.FC<RoomsTabProps> = ({ scheduleId, studioId }) => {
  const { data: rooms, isLoading } = useGetRoomsQuery({
    scheduleId,
    studioId,
  });
  const [createRoom, { isLoading: isCreating }] = useCreateRoomMutation();
  const [updateRoom, { isLoading: isUpdating }] = useUpdateRoomMutation();
  const [deleteRoom, { isLoading: isDeleting }] = useDeleteRoomMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [deleteDialogRoom, setDeleteDialogRoom] = useState<Room | null>(null);
  const [availabilityRoom, setAvailabilityRoom] = useState<Room | null>(null);

  // Form state
  const [formData, setFormData] = useState<RoomFormData>({
    room_name: '',
    is_combined: false,
    component_rooms: [],
    capacity: 20,
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<RoomFormErrors>({});

  const resetForm = () => {
    setFormData({
      room_name: '',
      is_combined: false,
      component_rooms: [],
      capacity: 20,
      notes: '',
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: RoomFormErrors = {};

    if (!formData.room_name.trim()) {
      errors.room_name = 'Room name is required';
    }
    if (formData.capacity !== undefined && formData.capacity < 0) {
      errors.capacity = 'Capacity must be non-negative';
    }
    if (
      formData.is_combined &&
      (!formData.component_rooms || formData.component_rooms.length === 0)
    ) {
      errors.component_rooms = 'Component rooms required for combined rooms';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      room_name: room.room_name,
      is_combined: room.is_combined,
      component_rooms: room.component_rooms || [],
      capacity: room.capacity,
      notes: room.notes || '',
    });
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setEditingRoom(null);
    resetForm();
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const dataToSend: RoomFormData = {
        room_name: formData.room_name,
        is_combined: formData.is_combined,
        capacity: formData.capacity,
        notes: formData.notes,
      };

      // Only include component_rooms if is_combined is true
      if (formData.is_combined && formData.component_rooms) {
        dataToSend.component_rooms = formData.component_rooms;
      }

      await createRoom({
        scheduleId,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to create room:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingRoom || !validateForm()) return;

    try {
      const dataToSend: RoomFormData = {
        room_name: formData.room_name,
        is_combined: formData.is_combined,
        capacity: formData.capacity,
        notes: formData.notes,
      };

      // Only include component_rooms if is_combined is true
      if (formData.is_combined && formData.component_rooms) {
        dataToSend.component_rooms = formData.component_rooms;
      }

      await updateRoom({
        id: editingRoom.id,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Failed to update room:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialogRoom) return;

    try {
      await deleteRoom({ id: deleteDialogRoom.id, studioId }).unwrap();
      setDeleteDialogRoom(null);
    } catch (error) {
      console.error('Failed to delete room:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Studio Rooms</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Room
        </Button>
      </Stack>

      {!rooms || rooms.length === 0 ? (
        <Alert severity="info">
          No rooms added yet. Click &quot;Add Room&quot; to create your first
          studio room.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Room Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Component Rooms</TableCell>
                <TableCell align="right">Availability</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map(room => (
                <TableRow key={room.id}>
                  <TableCell>{room.room_name}</TableCell>
                  <TableCell>
                    {room.is_combined ? (
                      <Chip label="Combined" color="primary" size="small" />
                    ) : (
                      <Chip label="Regular" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{room.capacity}</TableCell>
                  <TableCell>
                    {room.is_combined &&
                    room.component_rooms &&
                    room.component_rooms.length > 0
                      ? room.component_rooms.join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => setAvailabilityRoom(room)}
                      title="Manage availability"
                      color="primary"
                    >
                      <ScheduleIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(room)}
                      title="Edit room"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialogRoom(room)}
                      title="Delete room"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Room Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Room</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Room Name"
              value={formData.room_name}
              onChange={e =>
                setFormData({ ...formData, room_name: e.target.value })
              }
              error={!!formErrors.room_name}
              helperText={formErrors.room_name}
              required
            />
            <TextField
              fullWidth
              type="number"
              label="Capacity"
              value={formData.capacity}
              onChange={e =>
                setFormData({
                  ...formData,
                  capacity: parseInt(e.target.value, 10) || 0,
                })
              }
              error={!!formErrors.capacity}
              helperText={formErrors.capacity || 'Maximum number of students'}
              inputProps={{ min: 0 }}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_combined}
                  onChange={e =>
                    setFormData({ ...formData, is_combined: e.target.checked })
                  }
                />
              }
              label="Combined Room (e.g., rooms with accordion walls)"
            />
            {formData.is_combined && (
              <FormControl
                fullWidth
                required
                error={!!formErrors.component_rooms}
              >
                <InputLabel>Component Rooms</InputLabel>
                <Select<string[]>
                  multiple
                  value={formData.component_rooms || []}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const { value } = e.target;
                    setFormData({
                      ...formData,
                      component_rooms:
                        typeof value === 'string' ? value.split(',') : value,
                    });
                  }}
                  input={<OutlinedInput label="Component Rooms" />}
                  renderValue={(selected: string[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: string) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {rooms
                    ?.filter(room => !room.is_combined)
                    .map(room => (
                      <MenuItem key={room.id} value={room.room_name}>
                        {room.room_name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.component_rooms ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {formErrors.component_rooms}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Select the rooms that combine to form this room
                  </Typography>
                )}
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreating}
          >
            Add Room
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Room Dialog */}
      <Dialog
        open={!!editingRoom}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Room</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Room Name"
              value={formData.room_name}
              onChange={e =>
                setFormData({ ...formData, room_name: e.target.value })
              }
              error={!!formErrors.room_name}
              helperText={formErrors.room_name}
              required
            />
            <TextField
              fullWidth
              type="number"
              label="Capacity"
              value={formData.capacity}
              onChange={e =>
                setFormData({
                  ...formData,
                  capacity: parseInt(e.target.value, 10) || 0,
                })
              }
              error={!!formErrors.capacity}
              helperText={formErrors.capacity || 'Maximum number of students'}
              inputProps={{ min: 0 }}
              required
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_combined}
                  onChange={e =>
                    setFormData({ ...formData, is_combined: e.target.checked })
                  }
                />
              }
              label="Combined Room (e.g., rooms with accordion walls)"
            />
            {formData.is_combined && (
              <FormControl
                fullWidth
                required
                error={!!formErrors.component_rooms}
              >
                <InputLabel>Component Rooms</InputLabel>
                <Select<string[]>
                  multiple
                  value={formData.component_rooms || []}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const { value } = e.target;
                    setFormData({
                      ...formData,
                      component_rooms:
                        typeof value === 'string' ? value.split(',') : value,
                    });
                  }}
                  input={<OutlinedInput label="Component Rooms" />}
                  renderValue={(selected: string[]) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: string) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {rooms
                    ?.filter(
                      room => !room.is_combined && room.id !== editingRoom?.id
                    )
                    .map(room => (
                      <MenuItem key={room.id} value={room.room_name}>
                        {room.room_name}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.component_rooms ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {formErrors.component_rooms}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Select the rooms that combine to form this room
                  </Typography>
                )}
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Manager */}
      <AvailabilityManager
        open={!!availabilityRoom}
        onClose={() => setAvailabilityRoom(null)}
        entityType="room"
        entityId={availabilityRoom?.id || ''}
        entityName={availabilityRoom?.room_name || ''}
        studioId={studioId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogRoom}
        onClose={() => setDeleteDialogRoom(null)}
      >
        <DialogTitle>Delete Room?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;
            {deleteDialogRoom?.room_name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogRoom(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomsTab;
